// @ts-strict-ignore
import { DependencyList, useEffect, useMemo, useRef, useState } from 'react'

type ElementType<Element extends HTMLElement = HTMLElement> = Element | (() => Element)

type Dimensions = Omit<DOMRectReadOnly, 'toJSON'>

const getDimensions = (element?: HTMLElement): Dimensions => element?.getBoundingClientRect()

const emptyDimensions: Dimensions = {
	x: 0,
	y: 0,

	left: 0,
	right: 0,
	top: 0,
	bottom: 0,

	width: 0,
	height: 0,
}

const useDimensions = <Element extends HTMLElement = HTMLElement>(
	element?: ElementType<Element>,
	deps: DependencyList = [],
): Dimensions | undefined => {
	const elem = useMemo(() => (typeof element === 'function' ? element?.() : element), [element, ...deps])

	const [dimensions, setDimensions] = useState(getDimensions(elem))

	const resizeObserver = useRef(
		new ResizeObserver(entries => {
			for (const entry of entries) {
				if (entry.contentRect) {
					setDimensions(entry.contentRect)
				}
			}
		}),
	).current

	useEffect(() => {
		if (elem) {
			resizeObserver.observe(elem)
		}
		return () => {
			if (elem) {
				resizeObserver.unobserve(elem)
			}
		}
	}, [elem])

	return dimensions || emptyDimensions
}

export default useDimensions
